import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';

import { warehouseApi } from 'api/warehouse';
import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';
import { DataCell } from 'shared/components/bulkUpload/BulkUploadTableCells';
import { OUT_OF_STOCK_REASONS } from 'shared/constants/outOfStockReasons';

const TEMPLATE_CSV = 'Sellable Item ID,Unslotted Item OOS\n';
const TEMPLATE_FILENAME = 'unslotted_item_oos_template.csv';

const COLUMN_DEFS = [
    { key: 'sellableItemId', label: 'Sellable Item ID' },
    { key: 'unslottedItemOos', label: 'Unslotted Item OOS' },
    { key: 'reason', label: 'Reason', cell: DataCell },
];

const bulkApi = {
    edit: (key, params, rowsToUpdate, rowsToDelete) =>
        warehouseApi.editUnslottedItemOosBulk(key, params, rowsToUpdate, rowsToDelete),
    fetch: (key, params, signal) => warehouseApi.fetchUnslottedItemOosBulk(key, params, signal),
    persist: (key) => warehouseApi.persistUnslottedItemOosBulk(key),
    upload: (file, params) => warehouseApi.uploadUnslottedItemOosBulk(file, params),
};

function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = TEMPLATE_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Reviews a CSV of unslotted item out of stock flags, then persists the upload.
 * Items missing from the file are left unchanged.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {number|string} props.packGroupId
 * @param {(result: { successCount: number }) => void} [props.onSuccess]
 */
function BulkUnslottedItemOosUpload({ onOpenChange, onSuccess, open, packGroupId }) {
    const [reason, setReason] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const wasOpenRef = useRef(false);

    const params = useMemo(
        () => ({
            packGroupId,
            ...(reason ? { reason } : {}),
        }),
        [packGroupId, reason],
    );

    const bulkUpload = useBulkUpload({ api: bulkApi, params });
    const resetRef = useRef(bulkUpload.reset);
    resetRef.current = bulkUpload.reset;
    const hasData = bulkUpload.rows.length > 0;
    const isUploading = [BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(bulkUpload.status);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setReason('');
            setSelectedFile(null);
            resetRef.current();
        }
        wasOpenRef.current = open;
    }, [open, packGroupId]);

    const handleOpenChange = (nextOpen) => {
        if (isUploading && !nextOpen) {
            return;
        }
        onOpenChange(nextOpen);
    };

    const handleUpload = () => {
        if (!selectedFile || packGroupId == null) {
            return;
        }
        bulkUpload.uploadFile(selectedFile);
    };

    const handleSave = async () => {
        try {
            const result = await bulkUpload.persist();
            const errors = result?.persistResults?.errors ?? [];
            if (errors.length > 0) {
                return;
            }
            onSuccess?.({ successCount: result?.persistResults?.successCount ?? 0 });
            onOpenChange(false);
        } catch {
            // Persist failures are shown by BulkUploadStatus.
        }
    };

    const handleStartOver = () => {
        setSelectedFile(null);
        bulkUpload.reset();
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
                <DialogHeader>
                    <DialogTitle>Upload unslotted item out of stock</DialogTitle>
                    <DialogDescription>
                        Update unslotted item out of stock flags for sellable items in this pack group. Items that are
                        not in the file are left alone. Rows that are already in the requested state are skipped. If any
                        row is invalid, nothing is written.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>
                            CSV columns: <code className="rounded bg-muted px-1">Sellable Item ID</code> and{' '}
                            <code className="rounded bg-muted px-1">Unslotted Item OOS</code>.
                        </p>
                        <p>Use true, false, yes, or no. A blank flag is rejected. Maximum 2000 data rows.</p>
                        <p>
                            One reason applies to every out-of-stock row. A file that only marks items back in stock
                            does not need a reason.
                        </p>
                    </div>

                    {!hasData && (
                        <>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="unslotted-oos-reason">Reason</Label>
                                <Select value={reason} onValueChange={setReason} disabled={isUploading}>
                                    <SelectTrigger id="unslotted-oos-reason" className="w-full" aria-label="Reason">
                                        <SelectValue placeholder="Select a reason" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {OUT_OF_STOCK_REASONS.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-2">
                                    <Label>CSV file</Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={downloadTemplate}
                                        disabled={isUploading}
                                    >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download Template
                                    </Button>
                                </div>
                                <BulkUploadDropzone
                                    onFileSelect={setSelectedFile}
                                    disabled={isUploading}
                                    isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                                />
                            </div>

                            <div className="flex justify-end">
                                <Button type="button" onClick={handleUpload} disabled={!selectedFile || isUploading}>
                                    Upload
                                </Button>
                            </div>
                        </>
                    )}

                    <BulkUploadStatus
                        entityName="unslotted item out of stock flag"
                        fetchError={bulkUpload.fetchError}
                        handleStartNew={handleStartOver}
                        hasData={hasData}
                        persistError={bulkUpload.persistError}
                        persistErrors={bulkUpload.persistErrors}
                        persistingMessage="Saving unslotted item out of stock flags..."
                        status={bulkUpload.status}
                        uploadError={bulkUpload.uploadError}
                    />

                    {hasData && (
                        <div className="flex flex-col gap-4">
                            {bulkUpload.validationErrors.length > 0 && (
                                <div className="text-sm text-destructive">
                                    <p>Fix these errors before saving. Nothing is written while any row is invalid.</p>
                                    <ul className="mt-2 list-disc space-y-1 pl-4">
                                        {bulkUpload.validationErrors.map((error) => (
                                            <li key={`${error.row}:${error.field}:${error.code}`}>{error.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-end gap-2">
                                {bulkUpload.hasUnsavedChanges && (
                                    <>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={bulkUpload.clearPendingChanges}
                                        >
                                            Discard Changes
                                        </Button>
                                        <Button type="button" onClick={bulkUpload.submitChanges}>
                                            Submit Changes
                                        </Button>
                                    </>
                                )}
                                <Button type="button" variant="outline" onClick={handleStartOver}>
                                    Start over
                                </Button>
                                <Button type="button" onClick={handleSave} disabled={!bulkUpload.canPersist}>
                                    Save
                                </Button>
                            </div>
                            <BulkUploadTable
                                columnDefs={COLUMN_DEFS}
                                rows={bulkUpload.tableRows}
                                errorsByCell={bulkUpload.errorsByCell}
                                pendingEdits={bulkUpload.pendingEdits}
                                updateCell={bulkUpload.updateCell}
                                pendingDeletes={bulkUpload.pendingDeletes}
                                toggleRowDelete={bulkUpload.toggleRowDelete}
                            />
                            <BulkUploadPendingChanges
                                pendingDeletes={bulkUpload.pendingDeletes}
                                pendingEdits={bulkUpload.pendingEdits}
                                rowsLength={bulkUpload.rows.length}
                            />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export { BulkUnslottedItemOosUpload };
